import type {
  LSPathEntry,
  LSPathRequirementsMap,
} from "../../../../services/LeadershipSeminar/leadershipSeminarAPI.types";
import type { Requirement } from "./types";

// The MaintainFQOrGrowGPV requirement always requires +1 FQ growth; the displayed
// growth amount must not be driven by the API Target value (which carries the baseline count).
export const FQ_GROWTH_TARGET = 1;

const findRequirement = <T extends { Name: string }>(arr: T[] | undefined, name: string): T | undefined =>
  arr?.find((r) => r.Name === name);

/**
 * Build the interpolation params used to render a requirement's summary label.
 * Shared between the full LS path view page and the Annual Performance dashboard tile
 * so both surfaces render identical requirement text.
 */
export const getContentParams = (
  reqType: string,
  reqName: string,
  requirementsMap: LSPathRequirementsMap,
): Record<string, string> => {
  switch (reqType) {
    case "Volume": {
      const data = findRequirement(requirementsMap.Volume, reqName);
      return data ? { volume: data.Volume.Target.Formatted } : {};
    }
    case "NewLeg": {
      const data = findRequirement(requirementsMap.NewLeg, reqName);
      return data ? { target: String(data.Target) } : {};
    }
    case "QCount": {
      const data = findRequirement(requirementsMap.QCount, reqName);
      return data ? { target: String(data.Target), ytdQCount: String(data.YTDQCount) } : {};
    }
    case "MaintainFQOrGrowGPV": {
      const data = findRequirement(requirementsMap.MaintainFQOrGrowGPV, reqName);
      // The required FQ growth for this requirement is always +1, independent of the API Target value.
      return data ? { target: String(FQ_GROWTH_TARGET) } : {};
    }
    default:
      return {};
  }
};

/**
 * Build the compact Requirement[] summary list for a single path. Used to render the
 * requirement checklist shown on path cards and the dashboard tile.
 */
export const buildPathRequirements = (pathEntry: LSPathEntry, requirementsMap: LSPathRequirementsMap): Requirement[] =>
  pathEntry.Requirements.map((req) => ({
    ContentKey: `pathView.${req.ReqName}`,
    ContentParams: getContentParams(req.ReqType, req.ReqName, requirementsMap),
    Status: req.Status,
    Default: req.Status.every(Boolean),
  }));
